/**
 * KPIs Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { kpisApi } from '../../services/apiService';
import { queryKeys, STALE_TIMES } from '../../providers/QueryProvider';
import { KPIList, KPI } from '../../types';

interface KPIsListParams {
  page?: number;
  pageSize?: number;
  sourceType?: string;
  source?: number;
  initiative?: number;
  strategicGoal?: number;
  year?: number;
}

interface KPIsListResponse {
  results: KPIList[];
  count: number;
}

export function useKPIsQuery(params?: KPIsListParams) {
  return useQuery({
    queryKey: queryKeys.kpis.list(params),
    queryFn: async (): Promise<KPIsListResponse> => {
      const queryParams: Record<string, any> = {};
      if (params?.page) queryParams.page = params.page;
      if (params?.pageSize) queryParams.page_size = params.pageSize;
      if (params?.sourceType) queryParams.source_type = params.sourceType;
      if (params?.source) queryParams.source = params.source;
      if (params?.initiative) queryParams.initiative = params.initiative;
      if (params?.strategicGoal) queryParams.strategic_goal = params.strategicGoal;
      if (params?.year) queryParams.year = params.year;

      return await kpisApi.list(queryParams);
    },
    staleTime: STALE_TIMES.KPIS,
  });
}

export function useKPIDetailQuery(kpiId: number) {
  return useQuery({
    queryKey: queryKeys.kpis.detail(kpiId),
    queryFn: () => kpisApi.get(kpiId),
    enabled: !!kpiId,
    staleTime: STALE_TIMES.KPIS,
  });
}

export function useKPIReadingsQuery(kpiId: number) {
  return useQuery({
    queryKey: queryKeys.kpis.readings(kpiId),
    queryFn: () => kpisApi.getReadings(kpiId),
    enabled: !!kpiId,
    staleTime: STALE_TIMES.KPIS,
  });
}

export function useDeleteKPIMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (kpiId: number) => kpisApi.delete(kpiId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kpis.all });
    },
  });
}

export function useCreateKPIMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<KPI>) => kpisApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kpis.all });
    },
  });
}

export function useUpdateKPIMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<KPI> }) =>
      kpisApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kpis.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.kpis.detail(variables.id) });
    },
  });
}

export function useAddKPIReadingMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ kpiId, data }: { kpiId: number; data: any }) =>
      kpisApi.addReading(kpiId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kpis.readings(variables.kpiId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.kpis.detail(variables.kpiId) });
    },
  });
}

export default useKPIsQuery;
